/**
 * Challenges Page Types
 * Type definitions for the challenges listing page
 */

import type { ChallengeWithStats } from '../../../types/challenges';

export type FilterType = 'active' | 'ready' | 'pending' | 'finished';

/**
 * Tab options for Arena.
 * All = every section (Your Turn, Active, Pending, etc. + Finished).
 * Ready = Your Turn first, then Active. Pending = waiting for friends. Finished = history.
 */
export const FILTER_OPTIONS: Array<{ key: FilterType; label: string }> = [
  { key: 'active', label: 'All' },
  { key: 'ready', label: 'Ready' },
  { key: 'pending', label: 'Pending' },
  { key: 'finished', label: 'Finished' },
];

/** Section block: one container with header + all challenge cards inside (matches design). */
export type ArenaSectionItem = {
  type: 'section';
  sectionBucket: SectionBucket;
  title: string;
  challenges: ChallengeWithStats[];
};

/** Preview strip: up to 5 finished challenges shown in the All tab without any user interaction. */
export type ArenaFinishedPreviewItem = {
  type: 'historyPreview';
  challenges: ChallengeWithStats[];
};

/** Skeleton placeholder while history (or preview) is loading. */
export type ArenaFinishedSkeletonItem = {
  type: 'finishedSkeleton';
};

/** Flat list item for FlashList: one section (header + cards), history preview strip, skeleton, or load-more row */
export type ArenaListItem =
  | ArenaSectionItem
  | ArenaFinishedPreviewItem
  | ArenaFinishedSkeletonItem
  | { type: 'loadMore' };

/** Section bucket for home screen (each challenge appears in at most one) */
export type SectionBucket =
  | 'yourMove'
  | 'live'
  | 'waitingForFriends'
  | 'waitingForTaker'
  | 'waitingForOpponent'
  | 'active'
  | 'finished';

export interface ChallengeSectionBuckets {
  yourMove: ChallengeWithStats[];
  live: ChallengeWithStats[];
  waitingForFriends: ChallengeWithStats[];
  /** Your open (public-board) challenges that no one has taken yet. See specs/open-challenges. */
  waitingForTaker: ChallengeWithStats[];
  waitingForOpponent: ChallengeWithStats[];
  active: ChallengeWithStats[];
  finished: ChallengeWithStats[];
}

